import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Rocket, CheckCircle, Clock, Users, ChevronRight, Phone, Calendar } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Step Component
const StepCard = ({ number, title, description, icon: Icon }) => (
  <div className="relative flex gap-6">
    {/* Step Number */}
    <div className="flex flex-col items-center">
      <div 
        className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0"
        style={{ 
          background: "var(--primary-300)",
          color: "white",
          boxShadow: "0 4px 20px rgba(15, 95, 255, 0.4)"
        }}
      >
        {number}
      </div>
      {/* Connector line */}
      <div 
        className="w-0.5 flex-grow mt-4"
        style={{ background: "rgba(15, 95, 255, 0.3)", minHeight: "60px" }}
      />
    </div>
    
    <div className="pb-12 flex-grow">
      <div 
        className="card p-6"
        style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Icon size={24} style={{ color: "var(--primary-300)" }} />
          <h3 
            className="text-xl font-bold"
            style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
          >
            {title}
          </h3>
        </div>
        <p style={{ color: "var(--text-200)", lineHeight: "1.7" }}>{description}</p>
      </div>
    </div>
  </div>
);

// Benefit Item
const BenefitItem = ({ text }) => (
  <div className="flex items-center gap-3">
    <CheckCircle size={20} style={{ color: "var(--primary-300)", flexShrink: 0 }} />
    <span style={{ color: "var(--text-200)" }}>{text}</span>
  </div>
);

// Main Getting Started Page Component
const GettingStartedPage = () => {
  const steps = [
    {
      number: "1",
      title: "Dealership Setup",
      icon: Users,
      description: "Once your dealership is set up, users are added based on their role — salespeople, managers, and principals each see only what they need. From there, the app can be used immediately in the yard, in the office, or on the road."
    },
    {
      number: "2",
      title: "Start Appraising",
      icon: Rocket,
      description: "Salespeople can start appraising machines straight away, capturing all required details and photos as part of their normal sales process. The guided workflow ensures nothing is missed."
    },
    {
      number: "3",
      title: "Review & Value",
      icon: CheckCircle,
      description: "Managers and decision-makers receive appraisals ready for review and valuation without needing to chase information or log into multiple systems. Everything they need is in one place."
    },
    {
      number: "4",
      title: "Market & Sell",
      icon: Clock,
      description: "Once valued and approved, machines are ready to market with one click. Push to your chosen platforms and start reaching buyers immediately."
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.1) 0%, transparent 70%)"
            }}
          />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "var(--primary-100)", color: "white" }}
              >
                <Rocket size={18} />
                Quick Start
              </span>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Getting <span className="text-gradient">Started</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                Getting up and running with MachineryMarketed is{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>straightforward</span>. 
                No complex setup, no disruption, no long onboarding.
              </p>
            </div>
          </div>
        </section>

        {/* Simple Setup Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div 
                className="card p-10 text-center"
                style={{ 
                  background: "var(--background-200)", 
                  borderColor: "rgba(15, 95, 255, 0.2)"
                }}
              >
                <p 
                  className="text-xl"
                  style={{ color: "var(--text-100)", lineHeight: "1.8" }}
                >
                  MachineryMarketed is designed to fit neatly into how dealerships already operate. 
                  Because it works alongside your existing quoting and sales tools, there's nothing 
                  to replace or re-learn.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-16" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Setup Process
              </h2>
              
              {steps.map((step, index) => (
                <StepCard key={index} {...step} />
              ))}
              
              {/* Final note - no connector */}
              <div className="flex gap-6">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ 
                    background: "var(--accent-100)",
                    color: "var(--background-100)"
                  }}
                >
                  <CheckCircle size={24} />
                </div>
                <div 
                  className="card p-6 flex-grow"
                  style={{ background: "var(--background-300)", borderColor: "rgba(15, 95, 255, 0.3)" }}
                >
                  <h3 
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    You're Live!
                  </h3>
                  <p style={{ color: "var(--text-200)" }}>
                    Start seeing benefits almost immediately. Most dealerships are fully comfortable 
                    using MachineryMarketed within a day or two.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                What to Expect
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div 
                  className="card p-8"
                  style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
                >
                  <h3 
                    className="text-xl font-bold mb-6"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    The Goal
                  </h3>
                  <div className="space-y-4">
                    <BenefitItem text="Reduce admin time" />
                    <BenefitItem text="Improve accuracy" />
                    <BenefitItem text="Speed up trade flow" />
                    <BenefitItem text="Better team visibility" />
                  </div>
                </div>
                
                <div 
                  className="card p-8"
                  style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
                >
                  <h3 
                    className="text-xl font-bold mb-6"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    Support When You Need It
                  </h3>
                  <p 
                    className="mb-4"
                    style={{ color: "var(--text-200)", lineHeight: "1.7" }}
                  >
                    If you ever need help, support is available from people who understand 
                    dealership life and the machinery industry.
                  </p>
                  <p style={{ color: "var(--text-300)" }}>
                    Real people, real experience, real help.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="card p-12 text-center max-w-4xl mx-auto"
              style={{ 
                background: "linear-gradient(135deg, var(--background-300), var(--background-200))",
                borderColor: "rgba(15, 95, 255, 0.3)"
              }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Ready to Begin?
              </h2>
              <p 
                className="text-lg mb-8"
                style={{ color: "var(--text-200)" }}
              >
                Start your journey to faster trades and smoother operations today.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/faq" className="btn-outline">
                  Read FAQ
                </Link>
                <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
                  <Phone size={18} /> Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GettingStartedPage;
